import debounce from "lodash.debounce";

import { ChangeEvent, JSX, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import { useParams } from "react-router-dom";

import MovieCardSkeleton from "@/components/cards/MovieCard/MovieCard.skeleton.tsx";
import MoviesGrid from "@/components/helpers/MoviesGrid.tsx";
import { IconSadFace } from "@/components/icons";
import Input from "@/components/inputs/Input.tsx";

import { useGetCollectionDetail } from "@/hooks/api/content.hooks.ts";

const CollectionsSinglePage = (): JSX.Element => {
  const { t } = useTranslation();
  const { id } = useParams() as { id: string };

  const [search, setSearch] = useState<string>("");
  const { data, hasNextPage, fetchNextPage, isFetching } = useGetCollectionDetail({
    id: parseInt(id),
    limit: 48,
    search,
  });

  const { ref, inView } = useInView({ threshold: 0 });

  const debouncedSetSearch = debounce(setSearch, 1000);
  const handleSearch = (event: ChangeEvent<HTMLInputElement>): void => {
    debouncedSetSearch(event.target.value);
  };

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage]);

  const flattenData = useMemo(() => {
    return data ? data.pages.flatMap((page) => page.contents.result) : [];
  }, [data]);

  return (
    <section className={"rounded-7.5 py-10 max-lg:rounded-5 max-lg:py-6"}>
      <div
        className={"flex items-center justify-between max-md:w-full max-md:flex-col max-md:items-stretch max-md:gap-6"}
      >
        <h1 className={"text-4xl font-semibold max-md:text-3xl/10"}>{data?.pages[0].collection.title}</h1>
        <Input
          type={"search"}
          color={"white"}
          placeholder={t("Actions.search-key-words")}
          onChange={handleSearch}
          className={"w-[20.3125rem] py-3.5 max-md:w-full"}
        />
      </div>
      {flattenData && flattenData.length ? (
        <MoviesGrid content={flattenData} />
      ) : !isFetching ? (
        <div className={"flex flex-col items-center"}>
          <IconSadFace className={"size-20 fill-grey-1"} />
          <div className={"mt-4 max-w-[27.5rem] text-center"}>
            <h1 className={"text-2xl font-semibold"}>{t("error.nothing-found")}</h1>
            <p className={"mt-2 text-lg/6 text-on-surface"}>{t("error.nothing-found-message")}</p>
          </div>
        </div>
      ) : null}
      {isFetching ? (
        <div className={"movies-grid mt-2"}>
          {[...Array(12).keys()].map((_, index) => (
            <MovieCardSkeleton key={index} />
          ))}
        </div>
      ) : null}
      {flattenData.length ? <div ref={ref} className={"h-px w-full"} /> : null}
    </section>
  );
};

export default CollectionsSinglePage;
